import type en from "../en/marketing";
import type { Messages } from "../../types";

const fr: Messages<typeof en> = {
  stateDisabled: "Désactivé",
  stateExpired: "Expiré",
  stateUsedUp: "Épuisé",
  stateScheduled: "Programmé",
  stateActive: "Actif",

  discountsTitle: "Réductions",
  discountsDescription: "Codes que les clients saisissent lors du paiement. Un code par commande — les codes ne se cumulent jamais.",
  createDiscount: "Créer une réduction",
  notInPlan: "Les codes de réduction ne sont pas inclus dans votre forfait actuel. Passez à un forfait supérieur pour en créer.",
  discountDeleted: "Réduction supprimée.",
  noDiscounts: "Aucun code de réduction pour l’instant",
  noDiscountsHint: "Créez un code comme BIENVENUE10 pour récompenser les inscriptions à la newsletter ou lancer une promotion.",
  colCode: "Code",
  colValue: "Valeur",
  colUsed: "Utilisations",
  colWindow: "Période de validité",
  percentOff: "{value} % de réduction",
  amountOff: "{amount} de réduction",
  freeShipping: "Livraison gratuite",
  entireOrder: "Toute la commande",
  productsCount: "{count} produits",
  productsCount_one: "{count} produit",
  collectionsCount: "{count} collections",
  collectionsCount_one: "{count} collection",
  minSubtotalShort: "min. {amount}",
  perCustomer: "{count} par client",
  now: "Maintenant",
  noEnd: "Sans fin",

  editDiscount: "Modifier la réduction",
  newBreadcrumb: "Nouvelle",
  usedTimes: "Utilisé {count} fois",
  usedTimes_one: "Utilisé {count} fois",
  deleteTitle: "Supprimer {code} ?",
  deleteUsedBody: "Ce code a été utilisé sur des commandes : il sera désactivé au lieu d’être supprimé, afin de garder un historique des commandes exact.",
  deleteBody: "Le code est supprimé définitivement. Les clients ne pourront plus l’utiliser.",
  discountCreated: "Réduction créée. Partagez le code {code} avec vos clients.",

  percentRange: "Indiquez un pourcentage entre 0,01 et 100.",
  percentRangeField: "Indiquez un pourcentage entre 0,01 et 100",
  enterDiscountAmount: "Indiquez le montant de la réduction.",
  enterAmount: "Indiquez un montant",
  invalidStart: "Indiquez une date de début valide.",
  invalidEnd: "Indiquez une date de fin valide.",
  invalidDate: "Date non valide",
  discountSaved: "Réduction enregistrée.",
  deactivatedInstead: "Ce code a déjà été utilisé sur des commandes : il a été désactivé au lieu d’être supprimé (conservé pour l’historique des commandes et les rapports).",

  codeDescription: "Les clients le saisissent lors du paiement. Un seul code de réduction peut être utilisé par commande — les codes ne se cumulent pas.",
  discountCode: "Code de réduction",
  codeHint: "3 à 32 lettres, chiffres, - ou _. Insensible à la casse.",
  generate: "Générer",
  internalTitle: "Titre interne",
  internalTitleHint: "Visible par vous seul, ex. « Bienvenue newsletter ».",
  discountType: "Type de réduction",
  kindPercentage: "Pourcentage",
  kindPercentageHint: "ex. 10 % de réduction",
  kindFixed: "Montant fixe",
  kindFixedHint: "ex. 5,00 {currency} de réduction",
  kindFreeShippingHint: "Supprime les frais de livraison",
  percentageOff: "Pourcentage de réduction",
  amountOffLabel: "Montant de la réduction",
  amountOffHint: "Déduit des articles éligibles, sans jamais dépasser leur total.",
  appliesTo: "S’applique à",
  specificProducts: "Produits spécifiques",
  specificCollections: "Collections spécifiques",
  pickProductsEmpty: "Recherchez ci-dessus pour choisir des produits.",
  noCollections: "Vous n’avez encore aucune collection.",
  freeShippingEligible: "La livraison gratuite s’applique lorsque le panier contient un article éligible.",
  requirements: "Conditions et limites",
  minSubtotal: "Sous-total minimum",
  totalUses: "Utilisations totales",
  usedSoFar: "Utilisé {count} fois jusqu’à présent.",
  usedSoFar_one: "Utilisé {count} fois jusqu’à présent.",
  unlimitedHint: "Laissez vide pour un nombre illimité.",
  usesPerCustomer: "Utilisations par client",
  perCustomerHint: "Compté par adresse e-mail.",
  statusSchedule: "Statut et calendrier",
  timezoneHint: "Les heures sont dans le fuseau horaire de la boutique ({timezone}).",
  activeHint: "Désactivez pour suspendre le code sans le supprimer.",
  starts: "Début",
  startsHint: "Vide = immédiatement.",
  ends: "Fin",
  endsHint: "Vide = n’expire jamais.",

  marketingTitle: "Marketing",
  marketingDescription: "Reconquérez vos visiteurs et développez votre liste de diffusion.",
  abandonedReminders: "Relances de paniers abandonnés",
  paidPlans: "Forfaits payants",
  on: "Activé",
  off: "Désactivé",
  abandonedInfo: "Envoie une relance par e-mail à un visiteur qui a laissé des articles dans son panier. Les relances ne sont envoyées qu’aux visiteurs ayant accepté le marketing lors du paiement, au maximum une fois par panier, et chaque e-mail comporte un lien de désinscription.",
  abandonedNotInPlan: "Les relances de paniers abandonnés ne sont pas incluses dans le forfait {plan}. {upgrade} pour les activer.",
  upgrade: "Changez de forfait",
  sendAbandoned: "Envoyer des relances de paniers abandonnés",
  delayLabel: "Envoyer la relance après (heures)",
  delayHint: "Entre 1 et 72 heures d’inactivité du panier.",
  newsletterSignup: "Inscription à la newsletter",
  newsletterLabel: "Afficher un formulaire d’inscription à la newsletter sur votre boutique",
  newsletterHint: "Les visiteurs qui s’inscrivent sont ajoutés à votre liste d’abonnés avec leur date de consentement. Vous envoyez les campagnes avec votre propre outil d’e-mailing.",
  saveMarketing: "Enregistrer les paramètres marketing",
  marketingSaved: "Paramètres marketing enregistrés",
  subscribers: "Abonnés",
  activeSubscribers: "{n} abonnés actifs",
  activeSubscribers_one: "{n} abonné actif",
  exportCsv: "Exporter en CSV",
  noSubscribers: "Aucun abonné pour l’instant",
  noSubscribersHint: "Les personnes qui s’inscrivent à votre newsletter ou acceptent le marketing lors du paiement apparaîtront ici.",
  colSource: "Source",
  colSubscribed: "Inscrit le",
  sourceStorefront: "Boutique",
  sourceCheckout: "Paiement",
  showing100: "Affichage des 100 plus récents. Exportez le CSV pour obtenir la liste complète.",
  abandonedLast30: "Paniers abandonnés · 30 derniers jours",
  abandoned: "Abandonnés",
  abandonedDef: "Paniers contenant des articles, inactifs depuis plus d’une heure et non validés.",
  withEmail: "Avec e-mail",
  withEmailDef: "Paniers abandonnés pour lesquels le visiteur a saisi une adresse e-mail.",
  reminded: "Relancés",
  remindedDef: "Paniers ayant reçu un e-mail de relance.",
  recovered: "Récupérés",
  recoveredDef: "Paniers relancés qui ont ensuite abouti à une commande.",
  moreWays: "Autres leviers de croissance",
  discountsHint: "Codes et offres automatiques",
  seo: "SEO et redirections",
  seoHint: "Plan du site et redirections d’URL",
  analytics: "Statistiques",
  analyticsHint: "Ventes, tunnel de conversion et meilleurs produits",

  analyticsDescription: "Performances de vente en {currency}. Les commandes en mode test sont toujours exclues.",
  rangeToday: "Aujourd’hui",
  range7d: "7 j",
  range30d: "30 j",
  range90d: "90 j",
  range12m: "12 mois",
  keyFigures: "Chiffres clés",
  netSales: "Ventes nettes",
  grossSales: "Ventes brutes",
  discounts: "Réductions",
  discountsDef: "Total des réductions sur les commandes payées de la période (y compris les réductions de livraison gratuite).",
  refunds: "Remboursements",
  paidOrders: "Commandes payées",
  aov: "Panier moyen",
  totalCollected: "Total encaissé",
  afterRefunds: "{amount} après remboursements",
  pendingPayment: "Paiements en attente",
  pendingSub: "{count} commandes · non reçu",
  pendingSub_one: "{count} commande · non reçu",
  shippingCharged: "Livraison facturée",
  shippingDef: "Frais de livraison facturés sur les commandes payées.",
  taxCharged: "Taxes facturées",
  taxDef: "Taxes sur les commandes payées (incluses dans les prix ou ajoutées au paiement). Vous êtes responsable de leur reversement.",
  customers: "Clients",
  repeatCustomers: "Clients fidèles",
  repeatSub: "{repeat} sur {total} (depuis le début)",
  defPaidOrders: "Commandes avec un paiement confirmé (payées, partiellement remboursées, remboursées ou contestées) passées pendant la période. Les commandes en mode test sont exclues.",
  defGross: "Somme des sous-totaux des articles avant réductions, pour les commandes avec un paiement confirmé. Hors livraison et montants de taxe seuls. Commandes en mode test exclues.",
  defNet: "Ventes nettes = ventes brutes − réductions − remboursements émis pendant la période. Commandes en mode test exclues.",
  defAov: "Panier moyen = (ventes brutes − réductions) ÷ commandes payées.",
  defPending: "Total des commandes ouvertes encore en attente de paiement (ex. virement, paiement à la livraison, paiements en ligne inachevés). Cet argent n’a PAS été reçu.",
  defRefunds: "Remboursements réussis pendant la période, quelle que soit la date de la commande.",
  defCollected: "Montants encaissés par vos prestataires de paiement ou marqués comme reçus pour les paiements hors ligne, avant remboursements. Les frais du prestataire ne sont pas inclus car ils sont prélevés par votre prestataire.",
  defCustomers: "Adresses e-mail de clients distinctes ayant une commande payée pendant la période.",
  defRepeat: "Clients (depuis le début) ayant plus d’une commande payée, parmi tous les clients ayant une commande payée.",
  howCalculated: "Comment ces chiffres sont calculés",
  averageOrderValue: "Panier moyen",
  grossMargin: "Marge brute",
  grossMarginDef: "Chiffre d’affaires moins le coût, uniquement pour les articles dont vous avez saisi le prix de revient. Affiché « — » lorsqu’aucun coût n’est connu.",
  feesProfit: "Frais et bénéfice",
  feesProfitDef: "Les frais de traitement des paiements sont prélevés par votre propre prestataire et ne sont pas visibles par la plateforme : le bénéfice après frais n’est donc pas calculé.",
  salesByDay: "Ventes par jour",
  salesByDayHint: "Nettes de réductions, commandes payées, par date de commande (jours UTC).",
  noPaidOrders: "Aucune commande payée sur cette période.",
  advancedAnalytics: "Statistiques avancées",
  advancedHint: "Les meilleurs produits avec marges, la répartition par statut de paiement, le tunnel de conversion et les statistiques de paniers abandonnés sont inclus dans les forfaits Growth et Scale.",
  seePlans: "Voir les forfaits",
  askOwner: "Demandez au propriétaire de la boutique de changer de forfait.",
  topProducts: "Meilleurs produits",
  topProductsHint: "Par chiffre d’affaires (net des réductions sur articles) sur les commandes payées.",
  noSales: "Aucune vente sur cette période",
  colProduct: "Produit",
  colUnits: "Unités",
  colRevenue: "Chiffre d’affaires",
  noCostPrice: "Aucun prix de revient saisi",
  paymentStatus: "Statut de paiement",
  paymentStatusHint: "Toutes les commandes réelles passées pendant la période.",
  noOrdersPeriod: "Aucune commande sur cette période",
  funnel: "Tunnel de conversion",
  funnelHint: "Visiteurs uniques à chaque étape.",
  visitors: "Visiteurs",
  viewedProduct: "Ont consulté un produit",
  addedToCart: "Ont ajouté au panier",
  startedCheckout: "Ont commencé le paiement",
  placedOrder: "Ont passé commande",
  funnelNote: "Seules les visites enregistrées par le script de suivi de la boutique sont comptées. Les visiteurs qui bloquent les scripts ne sont pas inclus : le trafic réel peut donc être plus élevé. Les commandes de test peuvent apparaître dans « Ont passé commande ».",
  abandonedCarts: "Paniers abandonnés",
  abandonedCartsDef: "Paniers contenant des articles, inactifs depuis plus d’une heure, mis à jour pour la dernière fois pendant la période et non validés.",
  withEmailDefShort: "Paniers abandonnés pour lesquels le visiteur a saisi un e-mail.",
  remindersSent: "Relances envoyées",
  remindersSentDef: "Paniers abandonnés ayant reçu un e-mail de relance (visiteurs consentants uniquement).",
  recoveredDefLong: "Paniers relancés qui ont ensuite été convertis en commande.",
  feesAlert: "Les frais de traitement des paiements sont prélevés par votre propre prestataire de paiement et ne sont pas inclus. Consultez le tableau de bord de votre prestataire pour vos versements.",
};

export default fr;
